import { useEffect, useMemo, useState } from 'react'
import { FileText, Radar, Building, Percent, Upload, Download } from 'lucide-react'
import { Button } from '@components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@components/ui/table'
import { SortableTableHead, nextSort } from '@components/table-sort'
import { UploadEstimateModal } from '@components/adas-intelligence'
import { useAdasIntelligenceStore } from '@caradasstore/AdasIntelligenceStore'
import { useCenterStore } from '@caradasstore/CenterStore'
import { listScrubEstimates } from '../../../api/scrubApi'
import { getFriendlyErrorMessage } from '../../../utils/errorMessage'
import { AdasIntelligenceHeader, AdasIntelligenceTabs } from '../tabs'

const ANY = '__any__'

const DATE_RANGES = {
  'this-month': { label: 'This Month', days: null, mode: 'calendar' },
  'last-month': { label: 'Last Month', days: null, mode: 'last-month' },
  90: { label: 'Last 90 Days', days: 90 },
  ytd: { label: 'Year to Date', days: null, mode: 'ytd' }
}

function rangeFor(key) {
  const now = new Date()
  const def = DATE_RANGES[key] || DATE_RANGES['this-month']
  if (def.days) {
    const start = new Date(now.getTime() - def.days * 24 * 60 * 60 * 1000)
    return { start, end: now, label: def.label }
  }
  if (def.mode === 'calendar') {
    return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: now, label: def.label }
  }
  if (def.mode === 'last-month') {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59)
    return { start, end, label: def.label }
  }
  if (def.mode === 'ytd') {
    return { start: new Date(now.getFullYear(), 0, 1), end: now, label: def.label }
  }
  return { start: new Date(0), end: now, label: def.label }
}

const fmtShort = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

const KpiCard = ({ icon: Icon, label, value, sublabel, accent = 'blue' }) => (
  <div className='flex-1 rounded-xl border border-[#D1D5D8] bg-white p-5 shadow-sm'>
    <div className='mb-2 flex items-center justify-between'>
      <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>{label}</div>
      <div className={`flex h-8 w-8 items-center justify-center rounded-full ${accent === 'green' ? 'bg-[#D1FAE5]' : 'bg-[#DBEAFE]'}`}>
        <Icon className={`h-4 w-4 ${accent === 'green' ? 'text-[#059669]' : 'text-[#1F6FEB]'}`} />
      </div>
    </div>
    <div className='text-[28px] font-bold text-[#0A1628]'>{value}</div>
    <div className='mt-1 text-[12px] text-[#6B7280]'>{sublabel}</div>
  </div>
)

// ───────────────────────── Charts (inline SVG) ─────────────────────────

const ScanVolumeChart = ({ buckets }) => {
  const W = 520
  const H = 200
  const PAD_L = 32
  const PAD_R = 12
  const PAD_T = 12
  const PAD_B = 28
  const innerW = W - PAD_L - PAD_R
  const innerH = H - PAD_T - PAD_B
  const max = Math.max(1, ...buckets.map((b) => b.count))

  const points = buckets.map((b, i) => {
    const x = PAD_L + (innerW * i) / Math.max(1, buckets.length - 1)
    const y = PAD_T + innerH - (b.count / max) * innerH
    return [x, y]
  })
  const linePath = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x},${y}`).join(' ')
  const areaPath =
    points.length > 0
      ? `M${points[0][0]},${PAD_T + innerH} ${points.map(([x, y]) => `L${x},${y}`).join(' ')} L${points[points.length - 1][0]},${PAD_T + innerH} Z`
      : ''

  // 4 horizontal gridlines
  const gridY = [0, 0.25, 0.5, 0.75, 1].map((f) => PAD_T + innerH - f * innerH)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className='w-full' preserveAspectRatio='xMidYMid meet' role='img' aria-label='Scan volume over time'>
      <defs>
        <linearGradient id='sv-gradient' x1='0' x2='0' y1='0' y2='1'>
          <stop offset='0%' stopColor='#1F6FEB' stopOpacity='0.25' />
          <stop offset='100%' stopColor='#1F6FEB' stopOpacity='0' />
        </linearGradient>
      </defs>
      {gridY.map((y, i) => (
        <line key={i} x1={PAD_L} x2={W - PAD_R} y1={y} y2={y} stroke='#D1D5D8' strokeDasharray='2 3' />
      ))}
      {areaPath && <path d={areaPath} fill='url(#sv-gradient)' />}
      {linePath && <path d={linePath} fill='none' stroke='#1F6FEB' strokeWidth={2} />}
      {points.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={3} fill='#1F6FEB' />
      ))}
      {/* Y-axis labels */}
      {gridY.map((y, i) => (
        <text key={`yl-${i}`} x={PAD_L - 6} y={y + 3} textAnchor='end' fontSize={9} fill='#6B7280'>
          {Math.round((max * (4 - i)) / 4)}
        </text>
      ))}
      {/* X-axis labels (show every other) */}
      {buckets.map((b, i) =>
        i % Math.ceil(buckets.length / 6 || 1) === 0 ? (
          <text key={`xl-${i}`} x={points[i][0]} y={H - 8} textAnchor='middle' fontSize={9} fill='#6B7280'>
            {b.label}
          </text>
        ) : null
      )}
    </svg>
  )
}

const HorizontalBarChart = ({ data }) => {
  const W = 520
  const rowH = 22
  const PAD_L = 90
  const PAD_R = 30
  const H = Math.max(60, data.length * rowH + 10)
  const max = Math.max(1, ...data.map((d) => d.value))
  const innerW = W - PAD_L - PAD_R
  const colors = ['#0A1628', '#1F6FEB', '#6B7280', '#60A5FA', '#6B7280', '#1F6FEB', '#D1D5D8', '#1758C9']

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className='w-full' preserveAspectRatio='xMidYMid meet' role='img' aria-label='Calibrations by make'>
      {data.map((d, i) => {
        const y = i * rowH + 4
        const w = (d.value / max) * innerW
        return (
          <g key={d.label}>
            <text x={PAD_L - 6} y={y + 13} textAnchor='end' fontSize={10} fill='#475569'>
              {d.label}
            </text>
            <rect x={PAD_L} y={y} width={w} height={rowH - 8} rx={3} fill={colors[i % colors.length]} />
            <text x={PAD_L + w + 6} y={y + 13} fontSize={10} fill='#6B7280'>
              {d.value}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

// ───────────────────────── Page ─────────────────────────

/** Client Performance rows in the chosen column order; ties keep the leaderboard rank. */
export const sortLeaderboard = (rows, sort) => {
  if (!sort) return rows
  const sign = sort.dir === 'asc' ? 1 : -1
  const value = (row) => (sort.key === 'client' ? (row.client || '').trim().toLowerCase() : row[sort.key])
  return [...rows].sort((a, b) => {
    const va = value(a)
    const vb = value(b)
    if (va < vb) return -sign
    if (va > vb) return sign
    return a.rank - b.rank
  })
}

export const ReportsPage = () => {
  const kpiCounts = useAdasIntelligenceStore((s) => s.kpiCounts)
  const loadKpiCounts = useAdasIntelligenceStore((s) => s.loadKpiCounts)
  const ccId = useCenterStore((s) => s.ccId)

  const [uploadOpen, setUploadOpen] = useState(false)
  const [dateRange, setDateRange] = useState('this-month')
  const [client, setClient] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [rows, setRows] = useState([])

  const range = useMemo(() => rangeFor(dateRange), [dateRange])

  useEffect(() => {
    loadKpiCounts()
  }, [loadKpiCounts])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setIsLoading(true)
      setError(null)
      try {
        // API doesn't support date filters — pull a wide page and aggregate locally
        const params = { page: 0, size: 500 }
        if (ccId != null) params.ccId = ccId
        if (client) params.client = client
        const data = await listScrubEstimates(params)
        if (cancelled) return
        setRows(Array.isArray(data?.content) ? data.content : [])
      } catch (err) {
        if (!cancelled) setError(getFriendlyErrorMessage(err, 'Failed to load reports data'))
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [client, ccId])

  // Client list for dropdown (from loaded rows)
  const clientOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.client).filter(Boolean))
    return Array.from(set).sort()
  }, [rows])

  // Restrict data to date range
  const inRange = useMemo(() => {
    const startMs = range.start.getTime()
    const endMs = range.end.getTime()
    return rows.filter((r) => {
      const t = r.lastUpdated ? new Date(r.lastUpdated).getTime() : NaN
      return Number.isNaN(t) ? false : t >= startMs && t <= endMs
    })
  }, [rows, range])

  // Summary banner numbers
  const estimatesInRange = inRange.length
  const estimatedCalibrations = estimatesInRange * 2 // We don't have per-row cal counts in the list DTO, so the banner is a best-effort estimate

  // Scan Volume buckets — weekly within the selected range, or daily if range is short
  const volumeBuckets = useMemo(() => {
    if (inRange.length === 0) return []
    const start = range.start
    const end = range.end
    const spanDays = Math.max(1, Math.ceil((end - start) / (24 * 60 * 60 * 1000)))
    const bucketSize = spanDays <= 14 ? 1 : 7 // 1-day vs 7-day buckets
    const bucketCount = Math.min(14, Math.ceil(spanDays / bucketSize))
    const buckets = Array.from({ length: bucketCount }, (_, i) => {
      const bStart = new Date(end.getTime() - (bucketCount - i) * bucketSize * 24 * 60 * 60 * 1000)
      const bEnd = new Date(end.getTime() - (bucketCount - i - 1) * bucketSize * 24 * 60 * 60 * 1000)
      return {
        start: bStart,
        end: bEnd,
        count: 0,
        label: bucketSize === 1 ? bStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : `W${i + 1}`
      }
    })
    inRange.forEach((r) => {
      const t = new Date(r.lastUpdated).getTime()
      const idx = buckets.findIndex((b) => t >= b.start.getTime() && t < b.end.getTime())
      if (idx >= 0) buckets[idx].count += 1
    })
    return buckets
  }, [inRange, range])

  // Calibrations by Make — parse the first word of vehicleModel as the make heuristic
  const makeData = useMemo(() => {
    const map = {}
    inRange.forEach((r) => {
      if (!r.vehicleModel) return
      // Vehicle string looks like "2025 Toyota RAV4 XLE Hybrid" — skip year, take next word
      const parts = r.vehicleModel.split(/\s+/)
      const make = /^\d{4}$/.test(parts[0]) ? parts[1] : parts[0]
      if (!make) return
      map[make] = (map[make] || 0) + 1
    })
    return Object.entries(map)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8)
  }, [inRange])

  // Client leaderboard
  const clientLeaderboard = useMemo(() => {
    const map = {}
    inRange.forEach((r) => {
      const c = r.client || 'Unassigned'
      if (!map[c]) map[c] = { client: c, estimates: 0 }
      map[c].estimates += 1
    })
    return Object.values(map)
      .sort((a, b) => b.estimates - a.estimates)
      .slice(0, 10)
      .map((row, i) => ({ ...row, rank: i + 1 }))
  }, [inRange])
  // Column sort of the top 10 (client-side; the rank stays the leaderboard position).
  const [leaderboardSort, setLeaderboardSort] = useState(null)
  const sortedLeaderboard = useMemo(() => sortLeaderboard(clientLeaderboard, leaderboardSort), [clientLeaderboard, leaderboardSort])
  const handleLeaderboardSort = (key) => setLeaderboardSort((prev) => nextSort(prev, key))

  return (
    <div className='mx-auto max-w-[1400px] py-6'>
      <AdasIntelligenceHeader
        right={
          <button
            onClick={() => setUploadOpen(true)}
            className='inline-flex items-center gap-1.5 px-5 py-2.5 bg-sidebar text-white text-sm font-medium rounded-lg hover:bg-sidebar/90 transition-colors'
          >
            <Upload className='h-4 w-4' /> Upload Estimate
          </button>
        }
      />
      <AdasIntelligenceTabs />

      {/* Filters */}
      <div className='mb-4 flex flex-wrap items-center gap-3'>
        <div>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className='w-[220px]'>
              <SelectValue placeholder='Date Range' />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(DATE_RANGES).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className='mt-1 text-[11px] text-[#6B7280]'>
            {fmtShort(range.start)} – {fmtShort(range.end)}
          </div>
        </div>
        <Select value={client || ANY} onValueChange={(v) => setClient(v === ANY ? '' : v)}>
          <SelectTrigger className='w-[220px]'>
            <SelectValue placeholder='All Clients' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All Clients</SelectItem>
            {clientOptions.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary banner */}
      <div className='mb-6 rounded-lg border border-[#DBEAFE] bg-[#F0F7FF] px-4 py-3 text-sm text-[#1E40AF]'>
        In <span className='font-semibold'>{range.label}</span>, ADAS Intelligence processed{' '}
        <span className='font-semibold'>{estimatesInRange.toLocaleString()} estimates</span>
        {client ? (
          <>
            {' '}
            for <span className='font-semibold'>{client}</span>
          </>
        ) : null}
        .
      </div>

      {/* KPI cards */}
      <div className='mb-6 grid grid-cols-2 gap-4 md:grid-cols-4'>
        <KpiCard icon={FileText} label='Estimates Scanned' value={kpiCounts.total.toLocaleString()} sublabel='Total processed' />
        <KpiCard icon={Radar} label='In Range' value={estimatesInRange.toLocaleString()} sublabel={range.label} />
        <KpiCard icon={Building} label='Active Clients' value={clientOptions.length} sublabel='This period' />
        <KpiCard
          icon={Percent}
          label='Completion Rate'
          value={`${kpiCounts.total > 0 ? Math.round(((kpiCounts.reviewedCount + kpiCounts.quoteSentCount) / kpiCounts.total) * 100) : 0}%`}
          sublabel='Reviewed or quoted'
          accent='green'
        />
      </div>

      {error && <div className='mb-4 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 text-sm text-[#B91C1C]'>{error}</div>}

      {/* Charts */}
      <div className='mb-6 grid grid-cols-1 gap-4 md:grid-cols-2'>
        <div className='rounded-xl border border-[#D1D5D8] bg-white p-4 shadow-sm'>
          <div className='mb-2 flex items-center justify-between'>
            <h3 className='text-[14px] font-semibold text-[#0A1628]'>Scan Volume</h3>
            <span className='text-[11px] text-[#6B7280]'>
              {volumeBuckets.length} bucket{volumeBuckets.length === 1 ? '' : 's'}
            </span>
          </div>
          {volumeBuckets.length > 0 ? (
            <ScanVolumeChart buckets={volumeBuckets} />
          ) : (
            <div className='flex h-[200px] items-center justify-center text-sm text-[#6B7280]'>No data in range</div>
          )}
        </div>
        <div className='rounded-xl border border-[#D1D5D8] bg-white p-4 shadow-sm'>
          <h3 className='mb-2 text-[14px] font-semibold text-[#0A1628]'>Calibrations by Make</h3>
          {makeData.length > 0 ? (
            <HorizontalBarChart data={makeData} />
          ) : (
            <div className='flex h-[200px] items-center justify-center text-sm text-[#6B7280]'>No data in range</div>
          )}
        </div>
      </div>

      {/* Client leaderboard */}
      <div className='rounded-xl border border-[#D1D5D8] bg-white shadow-sm'>
        <div className='flex items-center justify-between border-b border-[#D1D5D8] px-4 py-3'>
          <h3 className='text-[14px] font-semibold text-[#0A1628]'>Client Performance</h3>
          <Button
            variant='ghost'
            size='sm'
            className='text-[#1F6FEB]'
            onClick={() => {
              const csv = [
                'Rank,Client,Estimates',
                ...clientLeaderboard.map((r, i) => `${i + 1},${JSON.stringify(r.client)},${r.estimates}`)
              ].join('\n')
              const blob = new Blob([csv], { type: 'text/csv' })
              const url = URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = 'adas-intelligence-clients.csv'
              a.click()
              URL.revokeObjectURL(url)
            }}
          >
            <Download className='mr-1.5 h-4 w-4' />
            Export CSV
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow className='bg-gradient-to-r from-[#2C3D4D] to-[#1F2D3D] hover:from-[#2C3D4D] hover:to-[#1F2D3D]'>
              <SortableTableHead
                label='Rank'
                sortKey='rank'
                sort={leaderboardSort}
                onSort={handleLeaderboardSort}
                className='w-16 text-white'
              />
              <SortableTableHead
                label='Client'
                sortKey='client'
                sort={leaderboardSort}
                onSort={handleLeaderboardSort}
                className='text-white'
              />
              <SortableTableHead
                label='Estimates'
                sortKey='estimates'
                sort={leaderboardSort}
                onSort={handleLeaderboardSort}
                className='text-right text-white'
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {clientLeaderboard.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className='py-8 text-center text-sm text-[#6B7280]'>
                  {isLoading ? 'Loading...' : 'No data in range'}
                </TableCell>
              </TableRow>
            ) : (
              sortedLeaderboard.map((row) => (
                <TableRow key={row.client}>
                  <TableCell className='font-mono text-[#6B7280]'>#{row.rank}</TableCell>
                  <TableCell className='text-[#0A1628]'>{row.client}</TableCell>
                  <TableCell className='text-right font-semibold text-[#0A1628]'>{row.estimates}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <UploadEstimateModal open={uploadOpen} onOpenChange={setUploadOpen} />
    </div>
  )
}
